import {
  Archive, ArrowRight, Folder, MoreHorizontal, PauseCircle, Plus, RotateCcw, Search, Workflow as WorkflowIcon,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Switch, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTenants } from "@/lib/queries";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";
import { folderNames, inFolder, nextWorkflowName, pickedFolder, statusBadge, type WorkflowSummary } from "@/lib/workflows";
import {
  useCreateWorkflow, useTenantWorkflowsPause, useTenantWorkflowsPaused, useWorkflowAction, useWorkflows,
} from "@/lib/workflows-api";

/**
 * Workflows (ADR-111 in platform, ACL-367), laid out as GoHighLevel's Automation → Workflows is,
 * since that is the screen the floor already knows: the list with each workflow's status and how
 * many leads it has had, folders to narrow it, and "Create workflow", which makes a draft and opens
 * the builder on it at once (the name is changed there, at the top).
 *
 * Admins only, as core-api's routes are: a workflow texts leads in the tenant's name. Nothing is
 * deleted: a draft is archived. Publishing is done in the builder, where the confirmation says in
 * words what it will do. The tenant-wide pause lives on the Settings tab, out of the list's way.
 */
export function WorkflowsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "settings" ? "settings" : "workflows";
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const tenants = useTenants({ enabled: isSuperadmin });
  const pickTenant = isSuperadmin && (tenants.data?.length ?? 0) > 1;
  const [picked, setPicked] = React.useState<number>();
  const tenantId = pickTenant ? (picked ?? tenants.data?.[0]?.id) : undefined;
  const { data, isPending, error, refetch } = useWorkflows(tenantId, !isSuperadmin || !tenants.isPending);
  const create = useCreateWorkflow(tenantId);
  const action = useWorkflowAction();
  const [folder, setFolder] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [showArchived, setShowArchived] = React.useState(false);
  React.useEffect(() => {
    setFolder(null);
    setQuery("");
  }, [tenantId]);
  const [unpublishing, setUnpublishing] = React.useState<WorkflowSummary | null>(null);

  const all = data ?? [];
  const folders = folderNames(all);
  const activeFolder = pickedFolder(folder, folders);
  const needle = query.trim().toLowerCase();
  const archivedCount = all.filter((w) => w.status === "archived").length;
  const shown = all.filter(
    (w) =>
      (showArchived || w.status !== "archived") &&
      (activeFolder === null || inFolder(w, activeFolder)) &&
      (!needle || w.name.toLowerCase().includes(needle)),
  );
  // A superadmin has no tenant of their own: the one picked, or the only one there is.
  const pauseTenant = tenantId ?? user?.tenant_id ?? (isSuperadmin ? tenants.data?.[0]?.id : undefined) ?? null;
  const paused = useTenantWorkflowsPaused(pauseTenant ?? 0, pauseTenant !== null);

  const createWorkflow = async () => {
    try {
      const made = await create.mutateAsync({ name: nextWorkflowName(all), folder: activeFolder });
      navigate(`/workflows/${made.id}`);
    } catch (e) {
      toast.error("Could not create the workflow", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const act = async (w: WorkflowSummary, verb: "unpublish" | "archive" | "restore") => {
    try {
      await action.mutateAsync({ id: w.id, action: verb });
      toast.success(
        verb === "unpublish" ? "Unpublished" : verb === "archive" ? "Archived" : "Restored as a draft",
        w.name,
      );
      setUnpublishing(null);
    } catch (e) {
      toast.error("Could not change the workflow", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const createButton = (
    <Button variant="default" loading={create.isPending} disabled={isPending} onClick={() => void createWorkflow()}>
      <Plus />
      Create workflow
    </Button>
  );

  return (
    <Page>
      <PageHeader
        title="Workflows"
        description="Automatic steps for your leads: when something happens, wait, then text them."
        action={
          <div className="flex items-center gap-2">
            {pickTenant && (
              <Select value={String(tenantId)} onValueChange={(value) => setPicked(Number(value))}>
                <SelectTrigger aria-label="Tenant" className="w-52">
                  <SelectValue placeholder="Choose tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.data?.map((tenant) => (
                    <SelectItem key={tenant.id} value={String(tenant.id)}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {createButton}
          </div>
        }
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          const p = new URLSearchParams(params);
          if (next === "workflows") p.delete("tab");
          else p.set("tab", next);
          setParams(p, { replace: true });
        }}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="space-y-3">
          {paused.data?.paused && (
            <Callout tone="warning">
              Every workflow is paused: no lead starts one and no text is sent. Turn it back on from the Settings tab.
            </Callout>
          )}
          {all.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-64">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-subtle" aria-hidden />
                <Input
                  aria-label="Search workflows"
                  placeholder="Search workflows"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              {folders.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Folders">
                  <FolderChip label="All" selected={activeFolder === null} onClick={() => setFolder(null)} />
                  {folders.map((f) => (
                    <FolderChip key={f} label={f} folder selected={activeFolder === f} onClick={() => setFolder(f)} />
                  ))}
                </div>
              )}
              {archivedCount > 0 && (
                <label className="ml-auto inline-flex items-center gap-2 text-xs text-text-muted">
                  <Switch checked={showArchived} onCheckedChange={setShowArchived} aria-label="Show archived" />
                  Show archived ({archivedCount})
                </label>
              )}
            </div>
          )}

          <Card className="overflow-hidden">
            {isPending ? (
              <TableSkeleton cols={6} />
            ) : error ? (
              <ErrorState
                error={error}
                title="Could not load the workflows"
                action={
                  <Button variant="outline" onClick={() => void refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : all.length === 0 ? (
              <EmptyState
                icon={WorkflowIcon}
                title="Create your first workflow"
                description="For example: when a rep sets No answer, wait 1 hour, then text the lead. It stays a draft until you publish it."
                action={createButton}
              />
            ) : shown.length === 0 ? (
              archivedCount === all.length && !showArchived ? (
                <EmptyState
                  title="Every workflow is archived"
                  description="Turn on Show archived to see them, or create a new one."
                  action={createButton}
                />
              ) : (
                <EmptyState title="No workflow found" description="Try another name or folder." />
              )
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total enrolled</TableHead>
                    <TableHead className="text-right">Active enrolled</TableHead>
                    <TableHead>Last updated</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((w) => {
                    const badge = statusBadge(w);
                    return (
                      <TableRow
                        key={w.id}
                        className="cursor-pointer [&>td]:h-14"
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button, a, [role=menuitem]")) return;
                          navigate(`/workflows/${w.id}`);
                        }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-accent-3 text-accent-11" aria-hidden>
                              <WorkflowIcon className="size-4" />
                            </span>
                            <div className="min-w-0">
                              <Link to={`/workflows/${w.id}`} className="block truncate font-medium text-text hover:underline">
                                {w.name}
                              </Link>
                              <p className="truncate text-xs text-text-muted">
                                {w.starts_when?.label ?? "No trigger yet"}
                                {w.folder && ` · ${w.folder}`}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge tone={badge.tone}>{badge.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-text">{w.counts.enrolled}</TableCell>
                        <TableCell className="text-right tabular-nums text-text">{w.counts.active}</TableCell>
                        <TableCell className="text-xs text-text-muted" title={formatDateTime(w.updated_at)}>
                          {relativeTime(w.updated_at)}
                          {w.updated_by_name && <span className="block">by {w.updated_by_name}</span>}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${w.name}`}>
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => navigate(`/workflows/${w.id}`)}>
                                <ArrowRight />
                                Open
                              </DropdownMenuItem>
                              {w.status === "published" && (
                                <DropdownMenuItem onSelect={() => setUnpublishing(w)}>
                                  <PauseCircle />
                                  Unpublish
                                </DropdownMenuItem>
                              )}
                              {w.status === "draft" && (
                                <DropdownMenuItem onSelect={() => void act(w, "archive")}>
                                  <Archive />
                                  Archive
                                </DropdownMenuItem>
                              )}
                              {w.status === "archived" && (
                                <DropdownMenuItem onSelect={() => void act(w, "restore")}>
                                  <RotateCcw />
                                  Restore as a draft
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          {pauseTenant !== null ? <PauseAll tenantId={pauseTenant} /> : null}
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={unpublishing !== null}
        onOpenChange={(open) => !open && setUnpublishing(null)}
        title={`Unpublish ${unpublishing?.name ?? "this workflow"}?`}
        description={
          unpublishing?.counts.active
            ? `No new lead will start it, and the ${unpublishing.counts.active === 1 ? "lead" : `${unpublishing.counts.active} leads`} in it now leave it at once: they get no more texts from it.`
            : "No new lead will start it. You can publish it again later."
        }
        confirmLabel="Unpublish"
        loading={action.isPending}
        onConfirm={() => unpublishing && void act(unpublishing, "unpublish")}
      />
    </Page>
  );
}

function FolderChip({
  label, selected, onClick, folder = false,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  folder?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
        selected
          ? "border-accent-8 bg-accent-2 text-accent-11"
          : "border-border bg-surface text-text-muted hover:border-border-strong hover:text-text",
      )}
    >
      {folder && <Folder className="size-3.5" aria-hidden />}
      {label}
    </button>
  );
}

/** Every workflow in the organisation, stopped or running. While it is on, no lead starts a
 *  workflow and no step is taken; a text that falls too late during the pause is not sent. */
function PauseAll({ tenantId }: { tenantId: number }) {
  const toast = useToast();
  const paused = useTenantWorkflowsPaused(tenantId);
  const set = useTenantWorkflowsPause();
  const on = paused.data?.paused ?? false;
  if (paused.error) {
    return (
      <ErrorState
        error={paused.error}
        title="Could not load whether workflows are paused"
        action={
          <Button variant="outline" onClick={() => void paused.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  return (
    <Card className="flex items-center justify-between gap-4 px-5 py-4">
      <div>
        <p id="pause-workflows-label" className="text-sm font-medium text-text">
          Pause every workflow
        </p>
        <p className="text-xs text-text-muted">
          While this is on, no lead starts a workflow and no text is sent. Use it to stop everything at once.
        </p>
        {on && (
          <Callout tone="warning" className="mt-2">
            Paused. No workflow is texting anyone.
          </Callout>
        )}
      </div>
      <Switch
        checked={on}
        disabled={paused.isPending || set.isPending}
        aria-busy={paused.isPending}
        aria-labelledby="pause-workflows-label"
        onCheckedChange={(next) =>
          set.mutate(
            { tenantId, paused: next },
            {
              onSuccess: () => toast.success(next ? "Every workflow paused" : "Workflows resumed"),
              onError: (e) =>
                toast.error("Could not change the pause", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </Card>
  );
}
